import platform from "platform";

import { warehouseService } from "~/api/warehouse";
import envConstants from "~/config/envConstants";
import { PongData } from "~/features/andon/andon.types";

export async function fetchServerVersion(): Promise<{
  tag: string;
  hash: string;
} | null> {
  try {
    const response = await warehouseService.get<{ tag: string; hash: string }>(
      `/version.json?v=${Date.now()}`,
      { headers: { "Cache-Control": "no-cache" } }
    );
    return response.data;
  } catch {
    return null;
  }
}

export async function collectPongData({
  workstationDeviceId
}: {
  workstationDeviceId: string | null;
}): Promise<PongData> {
  // ------------------------------------------------------------------
  // NETWORK (Chromium-only)
  // ------------------------------------------------------------------
  let network: PongData["network"] | undefined;

  const conn =
    typeof navigator !== "undefined" && "connection" in navigator
      ? (navigator.connection as Partial<NetworkInformation>)
      : undefined;

  if (conn && (conn.effectiveType || conn.rtt || conn.downlink)) {
    network = {
      effectiveType: conn.effectiveType ?? "",
      rtt: conn.rtt ?? 0,
      downlink: conn.downlink ?? 0
    };
  }

  // ------------------------------------------------------------------
  // CPU (Universal)
  // ------------------------------------------------------------------
  const cpu: PongData["cpu"] | undefined =
    typeof navigator.hardwareConcurrency === "number"
      ? { cores: navigator.hardwareConcurrency }
      : undefined;

  // ------------------------------------------------------------------
  // User Agent (Universal)
  // ------------------------------------------------------------------
  const userAgent = navigator.userAgent;
  const parsed = platform.parse(userAgent);

  let osName = "Unknown";
  let osVersion = "";

  if (typeof parsed.os === "string") {
    osName = parsed.os;
  } else if (parsed.os && typeof parsed.os === "object") {
    osName = parsed.os.family ?? "Unknown";
    osVersion = parsed.os.version ?? "";
  }

  const userAgentParsed: PongData["userAgentParsed"] = {
    browser: parsed.name ?? "Unknown",
    version: parsed.version ?? "?",
    os: `${osName} ${osVersion}`.trim(),
    platform: parsed.product ?? "desktop",
    isMobile: ["iPhone", "iPad", "iPod", "Android"].includes(
      parsed.product ?? ""
    )
  };

  // ------------------------------------------------------------------
  // User-Agent Client Hints (Chromium-only)
  // ------------------------------------------------------------------
  let userAgentLowEntropy: PongData["userAgentLowEntropy"] | undefined;
  let userAgentHighEntropy: PongData["userAgentHighEntropy"] | undefined;

  if ("userAgentData" in navigator && navigator.userAgentData) {
    const ua = navigator.userAgentData;

    userAgentLowEntropy = {
      brands: ua.brands,
      mobile: ua.mobile,
      platform: ua.platform
    };

    if (ua.getHighEntropyValues) {
      const high = await ua.getHighEntropyValues([
        "architecture",
        "bitness",
        "model",
        "platform",
        "platformVersion",
        "uaFullVersion",
        "fullVersionList"
      ]);

      userAgentHighEntropy = {
        architecture: high.architecture || "",
        bitness: high.bitness || "",
        model: high.model || "",
        platform: high.platform || "",
        platformVersion: high.platformVersion || "",
        uaFullVersion: high.uaFullVersion || "",
        fullVersionList: high.fullVersionList || []
      };
    }
  }

  // ------------------------------------------------------------------
  // MEMORY (Chromium-only)
  // ------------------------------------------------------------------
  let memory: PongData["memory"] | undefined;
  if ("memory" in performance) {
    const perfWithMemory = performance as Performance & {
      memory: {
        usedJSHeapSize: number;
        totalJSHeapSize: number;
        jsHeapSizeLimit: number;
      };
    };
    memory = {
      usedJSHeapSize: perfWithMemory.memory.usedJSHeapSize,
      totalJSHeapSize: perfWithMemory.memory.totalJSHeapSize,
      jsHeapSizeLimit: perfWithMemory.memory.jsHeapSizeLimit
    };
  }

  // ------------------------------------------------------------------
  // Screen Information
  // ------------------------------------------------------------------
  const screen =
    typeof window !== "undefined" &&
    window.screen &&
    window.innerWidth !== undefined
      ? {
          width: window.screen.width,
          height: window.screen.height,
          availWidth: window.screen.availWidth,
          availHeight: window.screen.availHeight,
          pixelRatio: window.devicePixelRatio,
          innerWidth: window.innerWidth,
          innerHeight: window.innerHeight
        }
      : undefined;

  const timeZone =
    typeof Intl !== "undefined" && Intl.DateTimeFormat
      ? Intl.DateTimeFormat().resolvedOptions().timeZone || ""
      : "";
  const timeZoneOffsetMinutes =
    typeof Date !== "undefined" ? new Date().getTimezoneOffset() : undefined;

  // ------------------------------------------------------------------
  // FPS (Universal)
  // ------------------------------------------------------------------
  const fps = await measureFPS(20);

  // ------------------------------------------------------------------
  // Navigation Performance Metrics (Universal)
  // ------------------------------------------------------------------
  let navigation: PongData["navigation"] | undefined;
  if (typeof performance !== "undefined" && performance.getEntriesByType) {
    const navEntries = performance.getEntriesByType(
      "navigation"
    ) as PerformanceNavigationTiming[];
    if (navEntries.length > 0) {
      const navEntry = navEntries[0];
      navigation = {
        type: navEntry.type || "unknown",
        transferSize: navEntry.transferSize ?? 0,
        duration: Math.round(navEntry.duration) || 0
      };
    }
  }

  // ------------------------------------------------------------------
  // Server Version
  // ------------------------------------------------------------------
  const serverVersion = await fetchServerVersion();

  return {
    userAgent,
    userAgentParsed,
    cpu: cpu ?? { cores: 0 },
    fps: fps ?? 0,
    network: network ?? { effectiveType: "", rtt: 0, downlink: 0 },
    memory: memory ?? {
      usedJSHeapSize: 0,
      totalJSHeapSize: 0,
      jsHeapSizeLimit: 0
    },
    userAgentLowEntropy: userAgentLowEntropy ?? {
      brands: [],
      mobile: false,
      platform: ""
    },
    userAgentHighEntropy: userAgentHighEntropy ?? {
      architecture: "",
      bitness: "",
      model: "",
      platform: "",
      platformVersion: "",
      uaFullVersion: "",
      fullVersionList: []
    },
    screen: screen ?? {
      width: 0,
      height: 0,
      availWidth: 0,
      availHeight: 0,
      pixelRatio: 0,
      innerWidth: 0,
      innerHeight: 0
    },
    timeZone: timeZone ?? "",
    timeZoneOffsetMinutes: timeZoneOffsetMinutes ?? 0,
    workstationDeviceId: workstationDeviceId ?? "no workstation",
    clientVersion: envConstants.VERSION_TAG || envConstants.VERSION,
    serverVersion,
    navigation: navigation ?? {
      type: "unknown",
      transferSize: 0,
      duration: 0
    }
  };
}

/**
 * Measures frame rate by counting frames rendered within a given interval
 * using requestAnimationFrame.
 */
function measureFPS(numOfFramesToCount: number): Promise<number> {
  return new Promise((resolve) => {
    let framesCounted = 0;
    const start = performance.now();

    function handleNextFrame() {
      framesCounted++;
      if (framesCounted < numOfFramesToCount) {
        requestAnimationFrame(handleNextFrame);
      } else {
        const duration = performance.now() - start;
        const fps = (framesCounted * 1000) / duration;
        const roundedFPS = Math.round(fps * 100) / 100;
        resolve(roundedFPS);
      }
    }

    requestAnimationFrame(handleNextFrame);
  });
}
